import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import PageContainer from "./layout/PageContainer.js";
import { getNetlifyFunctionsBase } from "../utils/netlifyFunctions.js";
import { getFriendlyApiError } from "../utils/netlifyFunctionError.js";
import { getStoredReflections, setStoredReflections } from "../utils/reflectionStorage.js";
import { getStoredUserId } from "../utils/userSession.js";

const StepIndicator = ({ steps, currentStep }) => {
  const progress = Math.round(((currentStep + 1) / steps.length) * 100);
  return (
    <div className="step-indicator" role="progressbar" aria-valuenow={currentStep + 1} aria-valuemin={1} aria-valuemax={steps.length}>
      <div className="step-indicator__top">
        <span>Step {currentStep + 1} of {steps.length}</span>
        <span>{progress}%</span>
      </div>
      <div className="step-indicator__track">
        <span className="step-indicator__bar" style={{ width: `${progress}%` }} />
      </div>
      <div className="step-indicator__dots">
        {steps.map((step, index) => (
          <div
            key={step.key}
            className={`step-indicator__dot ${index === currentStep ? "step-indicator__dot--active" : ""}`}
          >
            <span className="step-indicator__label">{step.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const StepCard = ({ title, subtitle, instruction, icon, children }) => {
  return (
    <div className="step-card step-card--slide">
      <div className="step-card__header">
        {icon ? <span className="step-card__icon" aria-hidden="true">{icon}</span> : null}
        <h2>{title}</h2>
      </div>
      {subtitle ? <p className="step-card__prompt">{subtitle}</p> : null}
      {instruction ? <p className="step-card__instruction">{instruction}</p> : null}
      {children}
    </div>
  );
};

const HERO_TYPEWRITER_TEXT = "Stop reacting. Start thinking clearly.";

const RESPONSE_OPTIONS = [
  "Give me clarity on siutation",
  "How can I respond to this"
];

const ResponseStepBlock = ({ selectedOption, customResponse, onOptionSelect, onCustomChange }) => {
  const isOther = selectedOption === "Other" || (selectedOption && !RESPONSE_OPTIONS.includes(selectedOption));
  return (
    <div className="response-step">
      <p className="step-card__instruction">Choose your response deliberately rather than automatically.</p>
      <div className="response-options" role="group" aria-label="Response options">
        {RESPONSE_OPTIONS.map((opt) => (
          <label key={opt} className="response-option">
            <input
              type="radio"
              name="response"
              value={opt}
              checked={selectedOption === opt}
              onChange={() => onOptionSelect(opt)}
            />
            <span>{opt}</span>
          </label>
        ))}
        <label className="response-option">
          <input
            type="radio"
            name="response"
            value="Other"
            checked={isOther}
            onChange={() => onOptionSelect("Other")}
          />
          <span>Other</span>
        </label>
      </div>
      {isOther ? (
        <textarea
          className="personalized-textarea response-custom"
          rows={3}
          value={customResponse}
          onChange={(e) => onCustomChange(e.target.value)}
          placeholder="Describe your response."
        />
      ) : null}
    </div>
  );
};

const Toast = ({ message, onClose }) => {
  if (!message) return null;
  return (
    <div className="toast" role="status" aria-live="polite">
      <span>{message}</span>
      <button type="button" onClick={onClose} aria-label="Close notification">✕</button>
    </div>
  );
};

const GuidedReflection = ({ isSubscriber, verifiedEmail, embedded = false }) => {
  const navigate = useNavigate();
  const steps = useMemo(
    () => [
      { key: "event", label: "Event" },
      { key: "interpretation", label: "Interpretation" },
      { key: "evidence", label: "Evidence" },
      { key: "response", label: "Response" }
    ],
    []
  );

  const [currentStep, setCurrentStep] = useState(0);
  const [event, setEvent] = useState("");
  const [interpretation, setInterpretation] = useState("");
  const [evidence, setEvidence] = useState("");
  const [responseOption, setResponseOption] = useState("");
  const [responseCustom, setResponseCustom] = useState("");
  const [toast, setToast] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [showRiskEmergency, setShowRiskEmergency] = useState(false);
  const [heroTypeChars, setHeroTypeChars] = useState(0);

  useEffect(() => {
    if (embedded || heroTypeChars >= HERO_TYPEWRITER_TEXT.length) return undefined;
    const msPerChar = 38;
    const id = window.setTimeout(() => setHeroTypeChars((n) => n + 1), msPerChar);
    return () => window.clearTimeout(id);
  }, [heroTypeChars, embedded]);

  const responseDisplay = responseOption === "Other" || (responseOption && !RESPONSE_OPTIONS.includes(responseOption))
    ? responseCustom.trim() || responseOption
    : responseOption;

  const saveReflection = async () => {
    if (isSaving) return;
    setSaveError("");
    setIsSaving(true);

    const eventTrimmed = (event || "").trim();
    const interpretationTrimmed = (interpretation || "").trim();
    const evidenceTrimmed = (evidence || "").trim();
    const userChosenResponse = responseDisplay || responseOption;
    const responseTrimmed = (userChosenResponse || "").trim();

    if (!eventTrimmed) {
      setSaveError("Please share what happened with you — even a few words is enough.");
      setIsSaving(false);
      return;
    }
    if (!interpretationTrimmed) {
      setSaveError("Please enter your Interpretation.");
      setIsSaving(false);
      return;
    }
    if (!evidenceTrimmed) {
      setSaveError("Please enter the Evidence.");
      setIsSaving(false);
      return;
    }
    if (!responseTrimmed) {
      setSaveError("Please select or enter a Response.");
      setIsSaving(false);
      return;
    }

    const combinedEntry = [
      eventTrimmed,
      interpretationTrimmed,
      evidenceTrimmed,
      responseTrimmed
    ].join("\n\n");

    if (!combinedEntry.trim()) {
      setSaveError("Please add at least one line of content in each step.");
      setIsSaving(false);
      return;
    }

    const entry = {
      id: Date.now(),
      createdAt: new Date().toISOString(),
      event,
      interpretation,
      evidence,
      response: responseDisplay || responseOption
    };
    try {
      const localUserId = getStoredUserId();
      const existing = await getStoredReflections(localUserId || undefined);
      await setStoredReflections([entry, ...existing], localUserId || undefined);
    } catch (err) {
      console.error("Failed to save reflection locally", err);
    }

    if (verifiedEmail) {
      try {
        const response = await fetch(`${getNetlifyFunctionsBase()}/.netlify/functions/getPersonalizedInsights`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
          entry: combinedEntry,
          userResponse: userChosenResponse,
          email: verifiedEmail,
          mode: "deep_reflection"
        })
        });
        const responseText = await response.text();
        if (!response.ok) {
          let parsed = null;
          try {
            parsed = JSON.parse(responseText);
          } catch (_) {
            /* use fallback below */
          }
          setSaveError(getFriendlyApiError(parsed, responseText?.length < 200 ? responseText : "Unable to generate insight."));
          setIsSaving(false);
          return;
        }
        try {
          const data = JSON.parse(responseText);
          if (data?.risk_self_harm) {
            setShowRiskEmergency(true);
            setIsSaving(false);
            return;
          }
        } catch (_) {}
      } catch (error) {
        console.error("Save insight error:", error);
        setSaveError(error?.message || "Network error. Check connection and try again.");
        setIsSaving(false);
        return;
      }
    }

    setToast(verifiedEmail ? "Session saved." : "Saved locally. Sign in to store and get insights.");
    setCurrentStep(0);
    setIsSaving(false);
    navigate("/history");
  };

  const dismissRiskEmergency = () => {
    setShowRiskEmergency(false);
    setCurrentStep(0);
    setToast("Session saved.");
    navigate("/history");
  };

  const goNext = () => {
    if (currentStep === 0 && !(event || "").trim()) {
      setSaveError("Please share what happened with you — even a few words is enough.");
      return;
    }
    if (currentStep === 1 && !(interpretation || "").trim()) {
      setSaveError("Please enter your Interpretation.");
      return;
    }
    if (currentStep === 2 && !(evidence || "").trim()) {
      setSaveError("Please enter the Evidence.");
      return;
    }
    setSaveError("");
    setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
  };
  const goBack = () => {
    setSaveError("");
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };
  const stepsRef = useRef(null);

  const framework = (
    <>
      <div className="guided-reflection__stepper" ref={stepsRef}>
        <StepIndicator steps={steps} currentStep={currentStep} />
        <div className="step-panels">
          {currentStep === 0 ? (
            <div className="step-panel step-panel--active">
              <StepCard
                title="Event"
                subtitle="What actually happened? — What actually happen."
                instruction="In a few words, what's going on for you. Then add what happened on the outside, without assumptions or mind-reading."
              >
                <textarea
                  className="personalized-textarea clarity-textarea"
                  rows={5}
                  value={event}
                  onChange={(e) => setEvent(e.target.value)}
                  placeholder="e.g. Felt dismissed after class — I presented; professor only said “Okay, next group,” no other feedback."
                  title="Event — what actually happened"
                />
              </StepCard>
            </div>
          ) : null}

          {currentStep === 1 ? (
            <div className="step-panel step-panel--active">
              <StepCard
                title="Interpretation"
                subtitle="What story did you add to it?"
                instruction="What are you telling yourself this means?"
              >
                <textarea
                  className="personalized-textarea clarity-textarea"
                  rows={4}
                  value={interpretation}
                  onChange={(e) => setInterpretation(e.target.value)}
                  placeholder="e.g. They didn't like it. It wasn't strong enough. I embarrassed myself. Everyone else probably did better."
                />
              </StepCard>
            </div>
          ) : null}

          {currentStep === 2 ? (
            <div className="step-panel step-panel--active">
              <StepCard
                title="Evidence"
                subtitle="Does the story hold up to reality?"
                instruction="List specifics that support or challenge your interpretation."
              >
                <textarea
                  className="personalized-textarea clarity-textarea"
                  rows={4}
                  value={evidence}
                  onChange={(e) => setEvidence(e.target.value)}
                  placeholder="e.g. The professor was short on time — class was running late. Two other groups also received no comments. No critical feedback was given. The grading rubric has not been released yet."
                />
              </StepCard>
            </div>
          ) : null}

          {currentStep === 3 ? (
            <div className="step-panel step-panel--active">
              <StepCard
                title="Response"
                subtitle="How will you choose to act now?"
              >
                <ResponseStepBlock
                  selectedOption={responseOption}
                  customResponse={responseCustom}
                  onOptionSelect={setResponseOption}
                  onCustomChange={setResponseCustom}
                />
              </StepCard>
            </div>
          ) : null}
        </div>
      </div>

      <div className="step-actions">
        <button className="secondary-button" type="button" onClick={goBack} disabled={currentStep === 0}>
          Back
        </button>
        {currentStep < steps.length - 1 ? (
          <button className="primary-button" type="button" onClick={goNext}>
            Next →
          </button>
        ) : (
          <button className="secondary-button" type="button" onClick={saveReflection} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save session"}
          </button>
        )}
      </div>

      {saveError ? <p className="login-error">{saveError}</p> : null}
    </>
  );

  return (
    <>
      {showRiskEmergency && (
        <div className="risk-emergency-overlay" role="alertdialog" aria-labelledby="risk-emergency-title">
          <div className="insight-card insight-card--risk risk-emergency-card">
            <h3 id="risk-emergency-title">Seek immediate help</h3>
            <p className="insight-card__emergency">
              {[
                "If you are in crisis or may hurt yourself, please reach out for human help right now.",
                "In the United States, call or text 988 (Suicide & Crisis Lifeline), 24/7, for free confidential support.",
                "If you are outside the US, call your local emergency number or search for a suicide prevention helpline in your country (for example findahelpline.com).",
                "If you or someone else is in immediate danger, contact emergency services (for example 911 in the US).",
                "Faith and prayer can matter, but trained counselors and crisis lines exist so you are not alone in this moment—please use them."
              ].join("\n\n")}
            </p>
            <button type="button" className="risk-emergency-dismiss" onClick={dismissRiskEmergency}>
              I understand
            </button>
          </div>
        </div>
      )}
      {embedded ? (
        <div className="guided-reflection guided-reflection--embedded">{framework}</div>
      ) : (
        <PageContainer className="guided-reflection">
          <section className="hero-v2 deep-reflection-hero" aria-label="Deep Reflection">
            <h1 className="hero-v2__headline hero-v2__headline--typewriter" aria-label={HERO_TYPEWRITER_TEXT}>
              <span className="hero-v2__typewriter-line" aria-hidden="true">
                {HERO_TYPEWRITER_TEXT.slice(0, heroTypeChars)}
                <span className="hero-v2__typewriter-caret" aria-hidden="true" />
              </span>
            </h1>
            <div className="deep-reflection-intro">
              <div className="deep-reflection-intro__block">
                <h2 className="deep-reflection-intro__heading">What Deep Reflection is</h2>
                <p className="deep-reflection-intro__body">
                  A structured session for one situation. You build a full thought record instead of reacting from a
                  partial story.
                </p>
              </div>
              <div className="deep-reflection-intro__block">
                <h2 className="deep-reflection-intro__heading">The method</h2>
                <p className="deep-reflection-intro__body">
                  <strong>EIER</strong> — Event → Interpretation → Evidence → Response. You name what happened, what
                  you made it mean, the facts for and against that interpretation, then the response you choose next.
                </p>
              </div>
              <div className="deep-reflection-intro__block">
                <h2 className="deep-reflection-intro__heading">How it helps</h2>
                <p className="deep-reflection-intro__body">
                  It separates facts from inference, surfaces recurring patterns, and replaces automatic reaction with
                  a deliberate response when mental load is high.
                </p>
              </div>
            </div>
          </section>
          {framework}
        </PageContainer>
      )}
      <Toast message={toast} onClose={() => setToast("")} />
    </>
  );
};

export default GuidedReflection;
